import { useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'

import { Button, InputGroupInput, ProgressButton, Calendar } from '@/components/ui'
import { MixLayout } from '@/components/layout'
import { useWebSocketContext } from '@/contexts'

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['standard', 'schedule']),
  startedAt: z.string().optional(),
  numberOfDays: z.coerce.number().optional(),
}).refine((data) => {
  if (data.type === 'schedule') {
    return !!data.startedAt?.trim() && typeof data.numberOfDays === 'number'
  }
  return true
}, {
  message: 'Start date and number of days are required for schedules',
  path: ['startedAt'],
})

type FormData = z.infer<typeof formSchema>

export default function CreateMixPage() {
  const navigate = useNavigate()
  const { createMix } = useWebSocketContext()
  const nameInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: 'standard',
      startedAt: new Date().toISOString().split('T')[0],
      numberOfDays: 7,
    },
    mode: 'onChange',
  })

  const watchType = form.watch('type')

  const onSubmit = async (data: FormData) => {
    try {
      const request = {
        name: data.name,
        type: data.type,
        startedAt: data.type === 'schedule' ? data.startedAt : undefined,
        numberOfDays: data.type === 'schedule' ? data.numberOfDays : undefined,
      }

      await createMix(request)
      toast.success(`${data.name} created successfully.`)

      if (data.type === 'schedule') {
        navigate('/home')
      } else {
        navigate('/home')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create mix'
      toast.error(message)
    }
  }

  const handleCancel = () => {
    form.reset()
    navigate('/home')
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      (e.target as HTMLInputElement).blur()
    }
  }

  return (
    <MixLayout>
      <div className="flex flex-col min-h-0">
        {/* Sticky header */}
        <div className="sticky top-0 bg-background z-30 px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-foreground truncate">Create Mix</h1>
              <p className="text-xs text-muted-foreground">Add a standard or schedule mix</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="mx-auto w-full max-w-md">
            <div className="bg-background rounded-lg shadow-lg p-6 border border-border">
              <div className="mb-4">
                <h2 className="select-none text-2xl font-bold text-foreground">
                  New Mix
                </h2>
              </div>

              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="space-y-4">
                  {/* Type selector */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-foreground">
                      Type
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { form.setValue('type', 'standard', { shouldValidate: true }) }}
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium border transition-colors cursor-pointer ${
                          watchType === 'standard'
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-foreground border-border hover:bg-muted'
                        }`}
                      >
                        Standard
                      </button>
                      <button
                        type="button"
                        onClick={() => { form.setValue('type', 'schedule', { shouldValidate: true }) }}
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium border transition-colors cursor-pointer ${
                          watchType === 'schedule'
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-foreground border-border hover:bg-muted'
                        }`}
                      >
                        Schedule
                      </button>
                    </div>
                  </div>

                  {/* Name — always visible */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${form.formState.errors.name ? 'text-red-500' : 'text-foreground'}`}
                    >
                      Name
                    </label>
                    <Controller
                      name="name"
                      control={form.control}
                      render={({ field }) => (
                        <InputGroupInput
                          {...field}
                          autoFocus={true}
                          data-1p-ignore
                          onKeyUp={handleKeyUp}
                          placeholder="e.g. Weekly Meal Plan"
                          ref={nameInputRef}
                          type="text"
                          className={form.formState.errors.name ? 'border-red-500' : ''}
                        />
                      )}
                    />
                    {form.formState.errors.name && (
                      <p className="text-red-500 text-sm mt-1">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>

                  {/* Start Date — schedule only */}
                  {watchType === 'schedule' && (
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${form.formState.errors.startedAt ? 'text-red-500' : 'text-foreground'}`}
                      >
                        Start Date
                      </label>
                      <Controller
                        name="startedAt"
                        control={form.control}
                        render={({ field }) => (
                          <Calendar
                            value={field.value || new Date().toISOString().split('T')[0]}
                            onChange={field.onChange}
                            className={form.formState.errors.startedAt ? 'border-red-500' : ''}
                          />
                        )}
                      />
                      {form.formState.errors.startedAt && (
                        <p className="text-red-500 text-sm mt-1">
                          {form.formState.errors.startedAt.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Number of Days — schedule only */}
                  {watchType === 'schedule' && (
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${form.formState.errors.numberOfDays ? 'text-red-500' : 'text-foreground'}`}
                      >
                        Number of Days
                      </label>
                      <Controller
                        name="numberOfDays"
                        control={form.control}
                        render={({ field }) => (
                          <InputGroupInput
                            {...field}
                            onKeyUp={handleKeyUp}
                            min={1}
                            max={30}
                            type="number"
                            className={form.formState.errors.numberOfDays ? 'border-red-500' : ''}
                          />
                        )}
                      />
                      {form.formState.errors.numberOfDays && (
                        <p className="text-red-500 text-sm mt-1">
                          Must be between 1 and 30 days
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex w-full gap-2">
                    <Button
                      className="flex-1 text-foreground"
                      onClick={handleCancel}
                      type="button"
                      variant="outline"
                    >
                      Cancel
                    </Button>
                    <ProgressButton
                      className="flex-1"
                      disabled={!form.formState.isValid || form.formState.isSubmitting}
                      isLoading={form.formState.isSubmitting}
                    >
                      <span>{form.formState.isSubmitting ? 'Creating...' : 'Create'}</span>
                    </ProgressButton>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </MixLayout>
  )
}
